import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../services/api";
import PageSkeleton from "../components/ui/PageSkeleton";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import Avatar from "../components/ui/Avatar";
import {
  Person,
  Email,
  Phone,
  Business,
  Work,
  AttachMoney,
  CalendarToday,
  ArrowBack,
  Lock,
  VpnKey,
  ContentCopy,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
} from "@mui/material";

const validationSchema = Yup.object({
  firstName: Yup.string()
    .min(2, "First name must be at least 2 characters")
    .required("First name is required"),
  lastName: Yup.string()
    .min(2, "Last name must be at least 2 characters")
    .required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
  department: Yup.string().required("Department is required"),
  position: Yup.string().required("Position is required"),
  salary: Yup.number()
    .positive("Salary must be positive")
    .required("Salary is required"),
  dateOfJoining: Yup.date()
    .max(new Date(), "Date of joining cannot be in the future")
    .required("Date of joining is required"),
  employeeType: Yup.string()
    .oneOf(["full-time", "part-time", "contract"], "Invalid employee type")
    .required("Employee type is required"),
  dateOfLeaving: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .when("dateOfJoining", (dateOfJoining, schema) =>
      dateOfJoining
        ? schema.min(
            dateOfJoining,
            "Date of leaving cannot be before date of joining"
          )
        : schema
    ),
});

export default function EmployeeEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const isAdmin =
    authUser?.role?.toUpperCase() === "ADMIN" ||
    authUser?.role?.toUpperCase() === "HR";
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState(null); // { success, temporaryPassword, error }

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      department: "",
      position: "",
      salary: "",
      dateOfJoining: "",
      employeeType: "full-time",
      dateOfLeaving: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Transform form values to match backend API structure
        const fullName = `${values.firstName} ${values.lastName}`
          .replace(/\s+/g, " ")
          .trim();
        const payload = {
          name: fullName,
          email: values.email,
          phone: values.phone,
          department_id: values.department,
          position: values.position,
          designation: values.position, // Backend uses designation field
          salary: Number(values.salary),
          joining_date: values.dateOfJoining,
          employee_type: values.employeeType,
        };

        if (values.dateOfLeaving) {
          payload.leaving_date = values.dateOfLeaving;
          payload.status = "inactive";
        } else {
          payload.leaving_date = null;
          payload.status = "active";
        }

        await api.put(`/employees/${id}`, payload);
        toast.success("Employee updated successfully!");
        setTimeout(() => {
          navigate("/employees");
        }, 1500);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to update employee");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch departments
        const deptResponse = await api.get("/departments");
        const deptData = Array.isArray(deptResponse.data)
          ? deptResponse.data
          : Array.isArray(deptResponse.data?.data)
          ? deptResponse.data.data
          : [];
        setDepartments(deptData);

        // Fetch employee
        const response = await api.get(`/employees/${id}`);
        const payload = response.data;
        const employeeData =
          payload && typeof payload === "object" && !Array.isArray(payload)
            ? payload
            : payload?.data && typeof payload.data === "object"
            ? payload.data
            : null;

        if (!employeeData || !employeeData.id && !employeeData._id) {
          toast.error("Employee not found");
          navigate("/employees");
          return;
        }

        setEmployee(employeeData);

        // Get department_id from Employee model first, fallback to User model if Employee doesn't have it
        const departmentId = employeeData.department_id?.id || 
                             employeeData.department_id?._id || 
                             employeeData.department_id ||
                             employeeData.user_id?.department_id?.id ||
                             employeeData.user_id?.department_id?._id ||
                             employeeData.user_id?.department_id ||
                             "";

        formik.setValues({
          firstName: employeeData.user_id?.name?.split(" ")[0] || "",
          lastName:
            employeeData.user_id?.name?.split(" ").slice(1).join(" ") || "",
          email: employeeData.user_id?.email || "",
          phone: employeeData.phone || employeeData.user_id?.phone || "",
          department: departmentId,
          position: employeeData.position || employeeData.designation || "",
          salary: employeeData.salary || "",
          dateOfJoining: employeeData.joining_date?.split("T")[0] || "",
          employeeType: employeeData.employee_type || "full-time",
          dateOfLeaving: employeeData.leaving_date
            ? new Date(employeeData.leaving_date).toISOString().split("T")[0]
            : "",
        });
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.response?.data?.error || "Failed to load employee data";
        toast.error(errorMessage);
        console.error("Failed to fetch employee:", err);
        if (err.response?.status === 404) {
          navigate("/employees");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <PageSkeleton type="form" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="secondary"
              onClick={() => navigate("/employees")}
              className="flex items-center gap-2"
            >
              <ArrowBack className="w-5 h-5" />
              Back
            </Button>
          </div>
          <h1 className="text-4xl font-bold text-primary mb-2">
            Edit Employee
          </h1>
          <p className="text-gray-600">Update employee information</p>
        </div>

        {/* Employee Header */}
        {employee && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card mb-6"
          >
            <div className="flex items-center gap-6 p-6">
              <Avatar
                src={employee.user_id?.profile_image}
                name={employee.user_id?.name}
                size="lg"
              />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-primary mb-1">
                  {employee.user_id?.name || "Employee"}
                </h2>
                <p className="text-sm text-gray-500 mb-2">
                  {employee.position || employee.designation || "Employee"}
                </p>
                {employee.user_id?.loginMethod && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    <Lock className="w-3.5 h-3.5" />
                    Login: {employee.user_id.loginMethod}
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Form */}
        <form onSubmit={formik.handleSubmit}>
          {/* Personal Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card mb-6"
          >
            <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Person className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    First Name
                  </label>
                </div>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                      ? formik.errors.firstName
                      : ""
                  }
                  placeholder="Enter first name"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Person className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Last Name
                  </label>
                </div>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                      ? formik.errors.lastName
                      : ""
                  }
                  placeholder="Enter last name"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Email className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Email Address
                  </label>
                </div>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.email && Boolean(formik.errors.email)
                      ? formik.errors.email
                      : ""
                  }
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Phone Number
                  </label>
                </div>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phone && Boolean(formik.errors.phone)
                      ? formik.errors.phone
                      : ""
                  }
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </motion.div>

          {/* Employment Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-6"
          >
            <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
              Employment Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Business className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Department
                  </label>
                </div>
                <select
                  id="department"
                  name="department"
                  value={formik.values.department}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`input-field w-full ${
                    formik.touched.department && formik.errors.department
                      ? "border-red-500"
                      : ""
                  }`}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option
                      key={dept._id || dept.id}
                      value={dept._id || dept.id}
                    >
                      {dept.name}
                    </option>
                  ))}
                </select>
                {formik.touched.department && formik.errors.department && (
                  <p className="text-sm text-red-600 mt-1">
                    {formik.errors.department}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Work className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Position
                  </label>
                </div>
                <Input
                  id="position"
                  name="position"
                  type="text"
                  value={formik.values.position}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.position && Boolean(formik.errors.position)
                      ? formik.errors.position
                      : ""
                  }
                  placeholder="Enter position"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <AttachMoney className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Salary
                  </label>
                </div>
                <Input
                  id="salary"
                  name="salary"
                  type="number"
                  value={formik.values.salary}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.salary && Boolean(formik.errors.salary)
                      ? formik.errors.salary
                      : ""
                  }
                  placeholder="Enter salary"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Work className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Employee Type
                  </label>
                </div>
                <select
                  id="employeeType"
                  name="employeeType"
                  value={formik.values.employeeType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="input-field w-full"
                >
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                </select>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarToday className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Date of Joining
                  </label>
                </div>
                <Input
                  id="dateOfJoining"
                  name="dateOfJoining"
                  type="date"
                  value={formik.values.dateOfJoining}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.dateOfJoining &&
                    Boolean(formik.errors.dateOfJoining)
                      ? formik.errors.dateOfJoining
                      : ""
                  }
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarToday className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Date of Leaving (Optional)
                  </label>
                </div>
                <Input
                  id="dateOfLeaving"
                  name="dateOfLeaving"
                  type="date"
                  value={formik.values.dateOfLeaving}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.dateOfLeaving &&
                    Boolean(formik.errors.dateOfLeaving)
                      ? formik.errors.dateOfLeaving
                      : ""
                  }
                />
              </div>
            </div>
          </motion.div>

          {/* Account & Login (Admin only) */}
          {isAdmin &&
            (employee?.user_id?.id || employee?.user_id?._id) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="card mb-6"
            >
              <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
                Account & Login
              </h3>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    Login method:{" "}
                    <span className="font-medium text-gray-900">
                      {employee.user_id.loginMethod || "Unknown"}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {employee.user_id.loginMethod === "Outlook only"
                      ? "User can set a password in their Profile to enable email/password login, or you can set one below."
                      : employee.user_id.loginMethod === "None"
                      ? "User has no login method. Reset password to set one."
                      : "Reset password to generate a new temporary password for the user."}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setResetPasswordResult(null);
                    setResetPasswordModalOpen(true);
                  }}
                  disabled={resettingPassword}
                  className="flex items-center gap-2 min-w-[180px]"
                >
                  <VpnKey className="w-5 h-5" />
                  Reset Password
                </Button>
              </div>
            </motion.div>
          )}

          {/* Submit Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex justify-end gap-4"
          >
            <Button
              variant="secondary"
              onClick={() => navigate("/employees")}
              className="min-w-[120px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting}
              className="min-w-[200px]"
            >
              {formik.isSubmitting ? "Updating..." : "Update Employee"}
            </Button>
          </motion.div>
        </form>
      </motion.div>

      {/* Reset Password Modal - Custom dialog (no browser confirm popup) */}
      <Dialog
        open={resetPasswordModalOpen}
        onClose={() => {
          if (!resettingPassword) {
            setResetPasswordModalOpen(false);
            setResetPasswordResult(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {resetPasswordResult
            ? resetPasswordResult.success
              ? "Password Reset Successful"
              : "Password Reset Failed"
            : "Reset User Password"}
        </DialogTitle>
        <DialogContent>
          {!resetPasswordResult ? (
            <DialogContentText>
              Generate a new temporary password? The current password will be
              replaced. You will see the new password once — share it securely
              with the user and ask them to change it on first login.
            </DialogContentText>
          ) : resetPasswordResult.success ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Share this password with the user securely:
              </p>
              <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                <code className="flex-1 font-mono text-lg font-semibold text-primary break-all">
                  {resetPasswordResult.temporaryPassword}
                </code>
                <MuiButton
                  size="small"
                  startIcon={<ContentCopy className="w-4 h-4" />}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      resetPasswordResult.temporaryPassword
                    );
                    toast.success("Copied to clipboard");
                  }}
                >
                  Copy
                </MuiButton>
              </div>
              <p className="text-xs text-amber-700">
                Ask the user to change this password on first login.
              </p>
            </div>
          ) : (
            <DialogContentText color="error">
              {resetPasswordResult.error}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!resetPasswordResult ? (
            <>
              <MuiButton
                onClick={() => {
                  setResetPasswordModalOpen(false);
                }}
                disabled={resettingPassword}
              >
                Cancel
              </MuiButton>
              <MuiButton
                variant="contained"
                color="primary"
                onClick={async () => {
                  setResettingPassword(true);
                  try {
                    const userId =
                      employee.user_id.id || employee.user_id._id;
                    const res = await api.post(
                      `/users/${userId}/admin-reset-password`
                    );
                    const pwd =
                      res.data?.data?.temporaryPassword ||
                      res.data?.temporaryPassword;
                    setResetPasswordResult({
                      success: true,
                      temporaryPassword: pwd,
                    });
                  } catch (err) {
                    setResetPasswordResult({
                      success: false,
                      error:
                        err.response?.data?.message ||
                        err.response?.data?.error ||
                        "Failed to reset password",
                    });
                  } finally {
                    setResettingPassword(false);
                  }
                }}
                disabled={resettingPassword}
              >
                {resettingPassword ? "Resetting..." : "OK"}
              </MuiButton>
            </>
          ) : (
            <MuiButton
              variant="contained"
              onClick={async () => {
                setResetPasswordModalOpen(false);
                const wasSuccess = resetPasswordResult?.success;
                setResetPasswordResult(null);
                if (wasSuccess) {
                  try {
                    const response = await api.get(`/employees/${id}`);
                    const payload = response.data;
                    const employeeData =
                      payload && typeof payload === "object" && !Array.isArray(payload)
                        ? payload
                        : payload?.data && typeof payload.data === "object"
                        ? payload.data
                        : null;
                    if (employeeData) {
                      setEmployee(employeeData);
                    }
                  } catch (err) {
                    console.error("Failed to refetch employee after password reset:", err);
                    toast.error("Password reset succeeded but could not refresh view. Please refresh the page.");
                  }
                }
              }}
            >
              Close
            </MuiButton>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
